import * as Joi from 'joi';
import { BackgroundFormatTypes, ChartResponsiveLayout, ChartVizPropVersion, Operators, } from '../../types/prism';
import { commonV1DeprecatedProps } from './v1';
import { AxisPropertiesSchema, ChartPropertiesSchemaV2, numberFormatConfigSchema, } from './v2';
import { ColumnPropertiesSchemaV3 } from './v3';
const FontPropertiesSchema = Joi.object({
    __typename: Joi.string().valid('FontAttrs'),
    color: Joi.string().allow(null),
    bold: Joi.boolean().allow(null),
    italic: Joi.boolean().allow(null),
    strikeThrough: Joi.boolean().allow(null),
    underline: Joi.boolean().allow(null),
});
const ConditionalFormatConfigSchema = Joi.object({
    __typename: Joi.string().valid('ConditionalMetric'),
    operator: Joi.string().valid(...Object.values(Operators)),
    rangeValues: Joi.object({
        __typename: Joi.string().valid('Range'),
        min: Joi.number().unsafe(),
        max: Joi.number().unsafe(),
    }).allow(null),
    value: Joi.string().allow(null, ''),
    plotAsBand: Joi.boolean().allow(null),
    fontProperties: FontPropertiesSchema.allow(null),
    backgroundFormatType: Joi.string()
        .valid(...Object.values(BackgroundFormatTypes))
        .allow(null),
    gradientBackgroundAttrs: Joi.object({
        __typename: Joi.string().valid('GradientBackgroundAttrs'),
        backgroundFormatRange: Joi.array()
            .items(Joi.number())
            .allow(null),
        backgroundFormatMidpoint: Joi.number()
            .unsafe()
            .allow(null),
        colors: Joi.array()
            .items(Joi.string())
            .allow(null, ''),
    }).allow(null),
    solidBackgroundAttrs: Joi.object({
        __typename: Joi.string().valid('SolidBackgroundAttrs'),
        color: Joi.string().allow(null),
    }).allow(null),
    isHighlightRow: Joi.boolean().allow(null),
});
const ColumnPropertiesSchemaV4 = Joi.object({
    __typename: Joi.string().valid('ChartVizColumnProperties'),
    dataLabels: Joi.boolean().allow(null),
    conditionalFormatting: Joi.object({
        __typename: Joi.string().valid('ConditionalFormatting'),
        rows: Joi.array().items(ConditionalFormatConfigSchema),
    }).allow(null),
});
export const pivotFieldSchema = {
    __typename: Joi.string().valid('PivotField'),
    area: Joi.string(),
    dataField: Joi.string(),
    sortBy: Joi.string().allow(null, ''),
    sortBySummaryField: Joi.string().allow(null, ''),
    sortBySummaryPath: Joi.array().allow(null, ''),
    sortOrder: Joi.string().allow(null, ''),
    expanded: Joi.boolean().allow(null),
};
export const ChartSpecificObjectSchema = {
    markersEnabled: Joi.boolean().allow(null),
    isStackedAsPercent: Joi.boolean().allow(null),
    stackedAsPercentFormat: numberFormatConfigSchema.allow(null),
    pivotState: Joi.object({
        __typename: Joi.string().valid('PivotState'),
        rowExpandedPaths: Joi.array().items(Joi.object({
            __typename: Joi.string().valid('PivotPaths'),
            paths: Joi.array().items(Joi.string()),
        })),
        columnExpandedPaths: Joi.array().items(Joi.object({
            __typename: Joi.string().valid('PivotPaths'),
            paths: Joi.array().items(Joi.string()),
        })),
        fields: Joi.array()
            .items(Joi.object({
            ...pivotFieldSchema,
        }))
            .allow(null),
        useTreeLayout: Joi.boolean(),
        showPivotSummaries: Joi.boolean(),
    }).allow(null),
    hidePivotSummaries: Joi.boolean().allow(null),
    summaryMode: Joi.string().allow(null),
    useFlatLayout: Joi.boolean().allow(null),
    isHeatmapOverlayed: Joi.boolean().allow(null),
    summaryFormat: numberFormatConfigSchema.allow(null),
    __typename: Joi.string().valid('ChartSpecificProperties'),
};
const ChartPropertiesSchemaV4 = {
    __typename: Joi.string().valid('ChartProperties'),
    visibleSeriesNames: Joi.array()
        .items(Joi.string().allow(''))
        .allow(null),
    axisExtremes: Joi.object({
        __typename: Joi.string().valid('AxisExtremesProps'),
        x: Joi.array()
            .items(Joi.object({
            __typename: Joi.string().valid('RangeParam'),
            min: Joi.number().allow(null),
            max: Joi.number().allow(null),
        }))
            .allow(null),
        y: Joi.array()
            .items(Joi.object({
            __typename: Joi.string().valid('RangeParam'),
            min: Joi.number().allow(null),
            max: Joi.number().allow(null),
        }))
            .allow(null),
    }).allow(null),
    gridLines: Joi.object({
        __typename: Joi.string().valid('GridLineProperties'),
        xGridlineEnabled: Joi.boolean().allow(null),
        yGridlineEnabled: Joi.boolean().allow(null),
    }).allow(null),
    isZoomed: Joi.boolean().allow(null),
    mapviewport: Joi.object({
        __typename: Joi.string().valid('MapViewport'),
        center: Joi.array()
            .length(2)
            .items(Joi.number()),
        zoomLevel: Joi.number(),
    }).allow(null),
    responsiveLayoutPreference: Joi.string()
        .valid(ChartResponsiveLayout.AutoOff, ChartResponsiveLayout.AutoOn, ChartResponsiveLayout.UserPreferredOn, ChartResponsiveLayout.UserPreferredOff)
        .allow(null),
    chartSpecific: Joi.object(ChartSpecificObjectSchema)
        .pattern(/\d/, Joi.any())
        .allow(null),
    responsiveLayoutDisabled: Joi.boolean().allow(null),
    showLinearRegressionLine: Joi.boolean().allow(null),
    showStackedLabels: Joi.boolean().allow(null),
    allLabels: Joi.boolean().allow(null),
    dataSize: Joi.number().allow(null),
};
const SeriesColorSchema = Joi.object({
    __typename: Joi.string().valid('SeriesColor'),
    serieName: Joi.string().allow(''),
    color: Joi.string(),
});
const SeriesMultiColorMapSchema = Joi.object({
    __typename: Joi.string().valid('SeriesMultiColorMap'),
    serieName: Joi.string().allow(''),
    color: Joi.array().items(Joi.string().allow(null)),
});
const MultiColorSchema = Joi.object({
    __typename: Joi.string().valid('MultiSeriesColorMap'),
    serieName: Joi.string().allow(''),
    colorMap: Joi.array().items(SeriesMultiColorMapSchema),
});
class ClientStateV4Class {
}
const ClientStateV4Schema = Joi.object({
    __typename: Joi.string().valid('ChartVizProps'),
    axisProperties: Joi.array().items(Joi.object({
        __typename: Joi.string().valid('AxisObj'),
        id: Joi.string(),
        properties: AxisPropertiesSchema,
    })),
    columnProperties: Joi.alternatives()
        .match('one')
        .try(Joi.object().pattern(Joi.string(), ColumnPropertiesSchemaV3), Joi.array().items(Joi.object({
        __typename: Joi.string().valid('ChartColumnPropertyMap'),
        columnId: Joi.string(),
        columnProperty: ColumnPropertiesSchemaV4,
    }))),
    chartProperties: Joi.alternatives()
        .match('any')
        .try(ChartPropertiesSchemaV2, {
        ...ChartPropertiesSchemaV4,
        showRoundedNumbers: Joi.boolean().allow(null),
    }),
    custom_color_selector_array: Joi.array()
        .items(Joi.string())
        .allow(null),
    customColorSelectorArray: Joi.array()
        .items(Joi.string())
        .allow(null),
    seriesColors: Joi.alternatives()
        .match('one')
        .try(Joi.object().pattern(Joi.string().allow(''), Joi.string()), Joi.array().items(SeriesColorSchema)),
    systemSeriesColors: Joi.alternatives()
        .match('one')
        .try(Joi.object().pattern(Joi.string().allow(''), Joi.string()), Joi.array().items(SeriesColorSchema)),
    systemMultiColorSeriesColors: Joi.alternatives()
        .match('one')
        .try(Joi.object().pattern(Joi.string(), Joi.object().pattern(Joi.string(), Joi.array().items(Joi.string().allow(null)))), Joi.array().items(MultiColorSchema)),
    multiColorSeriesColors: Joi.alternatives()
        .match('one')
        .try(Joi.object().pattern(Joi.string(), Joi.object().pattern(Joi.string(), Joi.array().items(Joi.string().allow(null)))), Joi.array().items(MultiColorSchema)),
    version: Joi.string().valid(ChartVizPropVersion.V4),
    ...commonV1DeprecatedProps,
});
export { ClientStateV4Schema, ColumnPropertiesSchemaV4, ChartPropertiesSchemaV4, ConditionalFormatConfigSchema, SeriesColorSchema, MultiColorSchema, };
//# sourceMappingURL=v4.js.map